/**
 * DenseDayCell — the BON-142 dense calendar-picker day-cell rendering, opt-in
 * via {@link WorkshopCalendarPicker}'s `dense` prop (default OFF — the rebook
 * + dashboards calendars keep the original `DayCell`). A day with exactly one
 * event renders a fuller tinted `DenseSingleEventCard`; 2+ events render as
 * one-line `CompactEventPill`s capped at `DENSE_MAX_VISIBLE`, with a
 * "+N more" link for the rest. A total-count badge shows for any day with 2+
 * events. Both the link and the badge hand off to the caller's
 * `onDayOverflowClick` — there's no built-in popover here (unlike the default
 * `DayCell`'s `CalendarDayOverflow`); the booking picker opens a day-detail
 * drawer instead.
 *
 * The date itself is {@link DayNumber}, which carries the TODAY marker
 * (BON-146). Every sub-part lives in its own file — this cell is rendered 42
 * times per month and each piece is independently testable.
 */
import { isSameMonth } from 'date-fns';

import type { CalendarEvent } from './calendarEvent';
import { CompactEventPill } from './CompactEventPill';
import { DayCountBadge } from './DayCountBadge';
import { DayNumber } from './DayNumber';
import { DenseOverflowLink } from './DenseOverflowLink';
import { DenseSingleEventCard } from './DenseSingleEventCard';

/** Three compact pills fit a day cell without crowding; the rest fold into
 *  the "+N more" link (BON-142 dense mode — the default `DayCell` caps at 2
 *  for its richer, taller pills). */
export const DENSE_MAX_VISIBLE = 3;

export interface DenseDayCellProps {
  day: Date;
  month: Date;
  events: CalendarEvent[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onDayOverflowClick?: (day: Date, dayEvents: CalendarEvent[]) => void;
  height: number;
}

export function DenseDayCell({
  day,
  month,
  events,
  selectedId,
  onSelect,
  onDayOverflowClick,
  height,
}: DenseDayCellProps) {
  const inMonth = isSameMonth(day, month);
  const total = events.length;
  const visible = events.slice(0, DENSE_MAX_VISIBLE);
  const hiddenCount = Math.max(total - DENSE_MAX_VISIBLE, 0);
  const openDrawer = () => onDayOverflowClick?.(day, events);

  return (
    <div
      style={{
        height,
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid var(--nb-border)',
        margin: -0.5,
        padding: 4,
        background: inMonth ? 'transparent' : 'var(--nb-bg-2, rgba(0,0,0,.02))',
      }}
    >
      <div
        style={{
          flex: '0 0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 3,
        }}
      >
        <DayNumber day={day} inMonth={inMonth} />
        {total >= 2 && <DayCountBadge count={total} onClick={openDrawer} />}
      </div>
      <div style={{ flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }}>
        {total === 1 ? (
          <DenseSingleEventCard
            event={events[0]}
            selected={selectedId === events[0].id}
            onSelect={onSelect}
          />
        ) : (
          visible.map((e) => (
            <CompactEventPill
              key={e.id}
              event={e}
              selected={selectedId === e.id}
              onSelect={onSelect}
            />
          ))
        )}
      </div>
      <div style={{ flex: '0 0 auto', minHeight: 14 }}>
        {hiddenCount > 0 && <DenseOverflowLink hiddenCount={hiddenCount} onClick={openDrawer} />}
      </div>
    </div>
  );
}
